<template>
  <TransitionRoot as="template" :show="open">
    <Dialog class="fixed inset-0 z-[9999]" @close="$emit('close')">
      <TransitionChild
        as="div"
        enter="ease-out duration-300"
        enter-from="opacity-0"
        enter-to="opacity-100"
        leave="ease-in duration-200"
        leave-from="opacity-100"
        leave-to="opacity-0"
        class="fixed inset-0 bg-gray-500/75 transition-opacity"
      />

      <div class="fixed inset-0 flex items-center justify-center p-4 sm:p-6">
        <TransitionChild
          as="div"
          enter="ease-out duration-300"
          enter-from="opacity-0 translate-y-4 sm:translate-y-0 sm:scale-95"
          enter-to="opacity-100 translate-y-0 sm:scale-100"
          leave="ease-in duration-200"
          leave-from="opacity-100 translate-y-0 sm:scale-100"
          leave-to="opacity-0 translate-y-4 sm:translate-y-0 sm:scale-95"
          class="flex items-center justify-center w-full"
        >
          <DialogPanel
            class="relative transform rounded-lg bg-white px-6 pb-6 pt-5 text-left shadow-xl transition-all w-full max-w-lg"
          >
            <button
              type="button"
              class="absolute right-4 top-4 text-gray-400 hover:text-gray-600"
              @click="$emit('close')"
            >
              <span class="sr-only">{{ $gettext("Fermer") }}</span>
              <XMarkIcon class="h-6 w-6" />
            </button>

            <DialogTitle class="text-base/7 font-semibold text-gray-900 mb-6">
              {{ $gettext("Créer un notaire") }}
            </DialogTitle>

            <form @submit.prevent="onSubmit" class="space-y-5">
              <!-- Nom -->
              <div>
                <label class="block text-sm/6 font-medium text-gray-900">{{ $gettext("Nom") }}</label>
                <div class="mt-2">
                  <input
                    v-model="form.name"
                    type="text"
                    :placeholder="$gettext('Ex: Maître Dupont')"
                    class="block w-full rounded-md bg-white px-3 py-1.5 text-base text-gray-900 outline outline-1 -outline-offset-1 outline-gray-300 placeholder:text-gray-400 focus:outline focus:outline-2 focus:-outline-offset-2 focus:outline-indigo-600 sm:text-sm/6"
                  />
                </div>
                <p v-if="formErrors.name" class="mt-1 text-sm text-red-600">{{ formErrors.name }}</p>
              </div>

              <!-- Rue -->
              <div>
                <label class="block text-sm/6 font-medium text-gray-900">{{ $gettext("Rue") }}</label>
                <div class="mt-2">
                  <input
                    v-model="form.addressStreet"
                    type="text"
                    class="block w-full rounded-md bg-white px-3 py-1.5 text-base text-gray-900 outline outline-1 -outline-offset-1 outline-gray-300 placeholder:text-gray-400 focus:outline focus:outline-2 focus:-outline-offset-2 focus:outline-indigo-600 sm:text-sm/6"
                  />
                </div>
                <p v-if="formErrors.addressStreet" class="mt-1 text-sm text-red-600">{{ formErrors.addressStreet }}</p>
              </div>

              <!-- Ville + Code postal -->
              <div class="grid grid-cols-3 gap-4">
                <div class="col-span-2">
                  <label class="block text-sm/6 font-medium text-gray-900">{{ $gettext("Ville") }}</label>
                  <div class="mt-2">
                    <input
                      v-model="form.addressCity"
                      type="text"
                      class="block w-full rounded-md bg-white px-3 py-1.5 text-base text-gray-900 outline outline-1 -outline-offset-1 outline-gray-300 placeholder:text-gray-400 focus:outline focus:outline-2 focus:-outline-offset-2 focus:outline-indigo-600 sm:text-sm/6"
                    />
                  </div>
                  <p v-if="formErrors.addressCity" class="mt-1 text-sm text-red-600">{{ formErrors.addressCity }}</p>
                </div>
                <div>
                  <label class="block text-sm/6 font-medium text-gray-900">{{ $gettext("Code postal") }}</label>
                  <div class="mt-2">
                    <input
                      v-model="form.addressZipcode"
                      type="text"
                      class="block w-full rounded-md bg-white px-3 py-1.5 text-base text-gray-900 outline outline-1 -outline-offset-1 outline-gray-300 placeholder:text-gray-400 focus:outline focus:outline-2 focus:-outline-offset-2 focus:outline-indigo-600 sm:text-sm/6"
                    />
                  </div>
                  <p v-if="formErrors.addressZipcode" class="mt-1 text-sm text-red-600">{{ formErrors.addressZipcode }}</p>
                </div>
              </div>

              <!-- Pays -->
              <div>
                <label class="block text-sm/6 font-medium text-gray-900">{{ $gettext("Pays") }}</label>
                <div class="mt-2">
                  <select
                    v-model="form.addressCountryId"
                    class="block w-full rounded-md bg-white px-3 py-1.5 text-base text-gray-900 outline outline-1 -outline-offset-1 outline-gray-300 focus:outline focus:outline-2 focus:-outline-offset-2 focus:outline-indigo-600 sm:text-sm/6"
                  >
                    <option value="">{{ $gettext("Sélectionner un pays") }}</option>
                    <option v-for="country in countryOptions" :key="country.value" :value="country.value">
                      {{ country.label }}
                    </option>
                  </select>
                </div>
                <p v-if="formErrors.addressCountryId" class="mt-1 text-sm text-red-600">{{ formErrors.addressCountryId }}</p>
              </div>

              <p v-if="formErrors.base" class="text-sm text-red-600">{{ formErrors.base }}</p>

              <!-- Actions -->
              <div class="flex justify-end gap-x-3 pt-5 border-t border-gray-900/10">
                <button
                  type="button"
                  class="rounded-md bg-white px-3 py-2 text-sm font-semibold text-gray-900 shadow-sm ring-1 ring-inset ring-gray-300 hover:bg-gray-50"
                  @click="$emit('close')"
                >
                  {{ $gettext("Annuler") }}
                </button>
                <button
                  type="submit"
                  :disabled="saving"
                  class="rounded-md bg-indigo-600 px-3 py-2 text-sm font-semibold text-white shadow-sm hover:bg-indigo-500 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-indigo-600 disabled:opacity-50"
                >
                  {{ saving ? $gettext("Création...") : $gettext("Créer") }}
                </button>
              </div>
            </form>
          </DialogPanel>
        </TransitionChild>
      </div>
    </Dialog>
  </TransitionRoot>
</template>

<script setup>
import { ref, computed, reactive, watch } from "vue";
import { Dialog, DialogPanel, DialogTitle, TransitionChild, TransitionRoot } from "@headlessui/vue";
import { XMarkIcon } from "@heroicons/vue/24/outline";
import { useNotaryStore } from "@/stores/notary";
import { useCountryStore } from "@/stores/country";
import { useGettext } from "vue3-gettext";

const { $gettext } = useGettext();

const props = defineProps({
  open: { type: Boolean, required: true },
});
const emit = defineEmits(["close", "created"]);

const notaryStore = useNotaryStore();
const countryStore = useCountryStore();

const saving = ref(false);
const formErrors = reactive({});

const belgiumId = computed(() => {
  const be = countryStore.sortedCountries.find((c) => c.attributes.name === "Belgique");
  return be?.id || "";
});

const defaultCountryId = () => belgiumId.value || "";

const form = reactive({
  name: "",
  addressStreet: "",
  addressCity: "",
  addressZipcode: "",
  addressCountryId: "",
});

const countryOptions = computed(() =>
  countryStore.sortedCountries.map((country) => ({
    value: country.id,
    label: country.attributes.name,
  }))
);

watch(() => props.open, (val) => {
  if (!val) {
    resetForm();
  } else {
    form.addressCountryId = defaultCountryId();
  }
});

const resetForm = () => {
  form.name = "";
  form.addressStreet = "";
  form.addressCity = "";
  form.addressZipcode = "";
  form.addressCountryId = "";
  Object.keys(formErrors).forEach((key) => delete formErrors[key]);
};

const onSubmit = async () => {
  Object.keys(formErrors).forEach((key) => delete formErrors[key]);

  if (!form.name) formErrors.name = $gettext("est obligatoire");
  if (!form.addressStreet) formErrors.addressStreet = $gettext("est obligatoire");
  if (!form.addressCity) formErrors.addressCity = $gettext("est obligatoire");
  if (!form.addressZipcode) formErrors.addressZipcode = $gettext("est obligatoire");
  if (!form.addressCountryId) formErrors.addressCountryId = $gettext("est obligatoire");

  if (Object.keys(formErrors).length > 0) return;

  saving.value = true;

  const notaryPayload = {
    type: "notary",
    attributes: {
      name: form.name,
      addressStreet: form.addressStreet,
      addressCity: form.addressCity,
      addressZipcode: form.addressZipcode,
    },
    relationships: {
      addressCountry: { data: { id: form.addressCountryId } },
    },
  };

  await notaryStore.create(
    notaryPayload,
    (created) => {
      saving.value = false;
      resetForm();
      emit("created", created);
      emit("close");
    },
    (errors) => {
      saving.value = false;
      if (errors.value) {
        Object.assign(formErrors, errors.value);
      }
    }
  );
};
</script>
